import React from 'react';
import { Link, Image, MaskedImage, RichText } from '@/components';
import type { ImageAsset, PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import { LinkInterface } from '@/types';

interface ImageWithMask {
  alt?: string;
  image: {
    asset: ImageAsset;
  };
  mask: string;
}

interface FeaturedEntry {
  _id: string;
  _type: string;
  title: string;
  image?: ImageWithMask;
  subtitle?: string;
  footer?: PortableTextBlockType;
  blurb?: PortableTextBlockType;
  includeCta?: boolean;
  cta?: LinkInterface;
  price?: number;
  duration?: string;
}

interface CustomProgramProps {
  id: string | number;
  entry: FeaturedEntry;
  imageRounded?: boolean;
  className?: string;
  imageContainerClass?: string;
  imageClass?: string;
  imageLayout?: string;
}

const CustomProgram: React.FC<CustomProgramProps> = ({
  entry,
  imageRounded,
  className,
  imageContainerClass,
  imageClass,
  imageLayout,
}) => (
  <div className={`h-auto ${className}`}>
    <div
      key={entry?._id}
      className={`flex ${imageLayout == 'imageBesideContent' ? 'flex-col gap-5 sm:flex-row md:flex-col lg:flex-row' : 'flex-col'}`}
    >
      {entry?.image && (
        <div
          className={`w-full ${imageContainerClass || ''} ${imageLayout == 'imageBesideContent' ? 'w-full sm:w-[150px] md:w-full lg:w-[200px]' : ''}`}
        >
          {entry?.image?.mask !== 'no-mask' ? (
            <MaskedImage
              image={entry?.image?.image}
              mask={entry?.image?.mask}
              className={`aspect-square w-full ${imageLayout == 'imageBesideContent' ? 'h-auto' : ''}`}
              alt={entry?.image?.alt || entry?.title}
              width={325}
            />
          ) : (
            <Image
              className={`${imageRounded ? 'rounded-2xl' : ''} ${imageClass}`}
              image={entry?.image?.image}
              alt={entry?.image?.alt || entry?.title}
              width={325}
            />
          )}
        </div>
      )}
      <div className={imageLayout == 'imageBesideContent' ? `flex-1` : ``}>
        <div className={imageLayout == 'imageBesideContent' ? 'mt-0' : 'mt-3'}>
          <h2 className='mb-1.5 text-xl font-semibold leading-9 sm:text-2xl'>{entry?.title}</h2>
          {entry?.subtitle && <h3 className='text-xl font-medium leading-7'>{entry?.subtitle}</h3>}
          {entry?.price && entry?.duration && (
            <p className='mb-0 mt-1 flex items-end'>
              <span className='text-lg font-normal leading-7 text-black'>${entry.price}</span>
              <span className='text-sm font-light leading-5 text-zinc-900'>/{entry.duration}</span>
            </p>
          )}
        </div>
        {entry?.blurb && (
          <div className='mt-2'>
            <RichText
              as={'p'}
              className='!text-lg font-light leading-relaxed'
              value={entry?.blurb}
            />
          </div>
        )}
        <hr
          className={`border-blackTrans15 ${imageLayout == 'imageBesideContent' ? 'mb-2 mt-3 sm:mt-4' : 'my-3 mt-5 sm:my-4'}`}
        />
        {entry?.footer && (
          <RichText
            as={'p'}
            className='list-style-enable list-square list-mb-3 pl-2 text-base font-light'
            value={entry?.footer}
          />
        )}
        {entry?.includeCta && entry?.cta && (
          <div className={imageLayout == 'imageBesideContent' ? 'mt-4' : 'mt-8'}>
            <Link
              {...entry?.cta}
              variant='default'
              size='sm'
            >
              {entry?.cta?.label}
            </Link>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default CustomProgram;
